<script setup lang="ts">
import { useAvatarPictures } from '@/composables/useAvatarPictures';
import { STUDENT_AVATAR_ICON_OPTIONS } from '@/constants';
import UserAvatarFallback from '@/core/UserAvatarFallback.vue';
import { IGenericUser } from '@/interfaces';
import { EIcon } from 'common/enum/misc.enum';
import { computed, onBeforeUnmount, ref, shallowRef, toRefs, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    user: IGenericUser | null;
    theme?: 'chart' | 'default';
  }>(),
  {
    theme: undefined,
  },
);
const { user, theme } = toRefs(props);

const { getAvatarPictureUrl } = useAvatarPictures();

const iconComponent = shallowRef(null);
const localAvatarPictureUrl = ref<string | null>(null);
const pictureLoaded = ref(false);

const avatar = computed(() => user.value?.avatar ?? '');
const avatarPictureFile = computed(() => user.value?.avatarPictureFile ?? null);
const avatarPictureId = computed(() => user.value?.avatarPictureId ?? null);
const avatarPictureUrl = computed(() => {
  if (localAvatarPictureUrl.value) return localAvatarPictureUrl.value;
  return getAvatarPictureUrl(avatarPictureId.value);
});

const isUserAvatarIcon = computed(() => STUDENT_AVATAR_ICON_OPTIONS.includes(avatar.value as EIcon));

const revokeLocalAvatarPictureUrl = (): void => {
  if (!localAvatarPictureUrl.value) return;
  URL.revokeObjectURL(localAvatarPictureUrl.value);
  localAvatarPictureUrl.value = null;
};

watch(
  avatar,
  async (newAvatar) => {
    if (newAvatar && isUserAvatarIcon.value) {
      iconComponent.value = (await import(`@/assets/icons/${newAvatar}Icon.vue`)).default;
    } else {
      iconComponent.value = null;
    }
  },
  { immediate: true },
);

watch(
  avatarPictureFile,
  (file) => {
    revokeLocalAvatarPictureUrl();
    if (file) {
      localAvatarPictureUrl.value = URL.createObjectURL(file);
    }
  },
  { immediate: true },
);

// Preload the picture and only swap it in once it is available; until then (or if it fails)
// the fallback chain below (initials / static avatar / icon) is shown. Preloading instead of
// rendering a hidden <img> keeps the component single-rooted, which the attribute fallthrough
// (e.g. `class="!size-button"` at call sites) depends on.
watch(
  avatarPictureUrl,
  (url) => {
    pictureLoaded.value = false;
    if (!url) return;
    const image = new Image();
    image.onload = () => {
      if (avatarPictureUrl.value === url) pictureLoaded.value = true;
    };
    image.src = url;
  },
  { immediate: true },
);

onBeforeUnmount(revokeLocalAvatarPictureUrl);
</script>

<template>
  <img
    v-if="avatarPictureUrl && pictureLoaded"
    :src="avatarPictureUrl"
    alt="User Avatar"
    class="rounded-full bg-surface-tint object-cover shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  />
  <UserAvatarFallback v-else-if="!avatar" :user="user" :theme="theme" />
  <img
    v-else-if="!isUserAvatarIcon"
    :src="`/assets/avatars/${avatar}.png`"
    alt="User Avatar"
    class="rounded-full bg-surface-tint shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  />
  <span
    v-else
    class="size-avatar rounded-full bg-surface-tint flex items-center justify-center shrink-0"
    :class="[theme === 'chart' && 'border-4 border-white']"
  >
    <component :is="iconComponent" class="size-[60%] text-tint" />
  </span>
</template>
